// Serviço para gerenciamento de administradores no Firebase Firestore.
// Collection: 'admins'
// Estrutura do documento:
// {
//   email: string (normalizado lowercase),
//   addedBy: string (email do admin que adicionou),
//   addedAt: timestamp,
//   displayName?: string (opcional)
// }

import {
  collection,
  getDocs,
  doc,
  getDoc,
  setDoc,
  deleteDoc,
  serverTimestamp,
  query,
  orderBy,
} from 'firebase/firestore';
import { db } from './firebase';

const ADMINS_COLLECTION = 'admins';
const isTest = import.meta.env.MODE === 'test';

/**
 * Normaliza e-mail para lowercase.
 */
function normalizeEmail(email) {
  if (!email || typeof email !== 'string') return '';
  return email.trim().toLowerCase();
}

/**
 * Busca todos os administradores cadastrados no Firestore.
 * @returns {Promise<Array<{email: string, addedBy?: string, addedAt?: number, displayName?: string}>>}
 */
export async function fetchAdmins() {
  if (!db) {
    throw new Error('Firestore não inicializado');
  }

  try {
    const adminsCol = collection(db, ADMINS_COLLECTION);
    const q = query(adminsCol, orderBy('addedAt', 'desc'));
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(docSnap => ({
      email: docSnap.id,
      ...docSnap.data(),
    }));
  } catch (error) {
    if (!isTest) console.error('Erro ao buscar admins:', error);
    throw error;
  }
}

/**
 * Verifica se um e-mail está cadastrado como admin no Firestore.
 * @param {string} email - E-mail a verificar
 * @returns {Promise<boolean>}
 */
export async function isAdminInFirestore(email) {
  if (!db) {
    return false;
  }

  const normalized = normalizeEmail(email);
  if (!normalized) return false;

  try {
    const docRef = doc(db, ADMINS_COLLECTION, normalized);
    const docSnap = await getDoc(docRef);
    return docSnap.exists();
  } catch (error) {
    if (!isTest) console.error('Erro ao verificar admin:', error);
    return false;
  }
}

/**
 * Adiciona um novo administrador ao Firestore.
 * @param {string} email - E-mail do novo admin (será normalizado)
 * @param {string} addedByEmail - E-mail do admin que está adicionando
 * @param {string} [displayName] - Nome de exibição (opcional)
 * @returns {Promise<void>}
 */
export async function addAdmin(email, addedByEmail, displayName = '') {
  if (!db) {
    throw new Error('Firestore não inicializado');
  }

  const normalized = normalizeEmail(email);
  if (!normalized) {
    throw new Error('E-mail inválido');
  }

  // Validação básica de e-mail
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(normalized)) {
    throw new Error('Formato de e-mail inválido');
  }

  try {
    // Usar e-mail normalizado como ID do documento
    const docRef = doc(db, ADMINS_COLLECTION, normalized);
    
    // Verificar se já existe
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      throw new Error('Admin já cadastrado');
    }

    const data = {
      email: normalized,
      addedBy: normalizeEmail(addedByEmail),
      addedAt: serverTimestamp(),
    };

    if (displayName && typeof displayName === 'string') {
      data.displayName = displayName.trim();
    }

    await setDoc(docRef, data);
  } catch (error) {
    if (!isTest) console.error('Erro ao adicionar admin:', error);
    throw error;
  }
}

/**
 * Remove um administrador do Firestore.
 * @param {string} email - E-mail do admin a remover
 * @returns {Promise<void>}
 */
export async function removeAdmin(email) {
  if (!db) {
    throw new Error('Firestore não inicializado');
  }

  const normalized = normalizeEmail(email);
  if (!normalized) {
    throw new Error('E-mail inválido');
  }

  try {
    const docRef = doc(db, ADMINS_COLLECTION, normalized);
    await deleteDoc(docRef);
  } catch (error) {
    if (!isTest) console.error('Erro ao remover admin:', error);
    throw error;
  }
}
